import * as yup from 'yup';
import { Check, Lock, Users, X } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';

import { AddOrderStatus, PaymentMethodInput } from '__generated__/graphql';

import { Event, Registration } from '../helpers';
import { containsKeyPrefix, getServerErrors } from '../../../common/helpers';
import GetRegistrationDetailsQuery from './GetRegistrationDetailsQuery';
import GetRegistrationSummaryForDashboardQuery from './GetRegistrationSummaryForDashboardQuery';
import PaymentMethodSelection from '../../../common/Payments/PaymentMethodSelection';
import ReplyToWaitlistInviteMutation from './ReplyToWaitlistInviteMutation';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useOnPageCacheRestore from '../../../common/useOnPageCacheRestore';
import usePaymentMethods from '../../../common/usePaymentMethods';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';
import useValidation from '../../../common/useValidation';

const respondToWaitlistInviteSchema = yup.object({
  accept: yup.bool().required(),
  payment_method: yup.object({
    payment_method: yup.string().ensure(),
  }).when(['accept'], {
    is: (value: boolean) => value,
    then: yup.object({
      payment_method: yup.string().ensure().required(),
    }),
  }).nullable(),
});

export interface RespondToWaitlistInviteFormProps {
  registration: Registration;
  event: Event;
  onReject: () => unknown;
}

const RespondToWaitlistInviteForm = ({ registration, event, onReject }: RespondToWaitlistInviteFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();

  const waitlistInvite = registration.waitlist_invite;
  const pendingInvite = waitlistInvite.pending_invite;

  const requirePayment = pendingInvite.total_amount > 0;
  const totalAmount = pendingInvite.total_amount + pendingInvite.fee;

  const form = useForm({
    values: {
      accept: undefined,
      payment_method: requirePayment ? {
        payment_method: null,
        issuer: null,
      } as PaymentMethodInput : null,
    },
  });

  // Used to disable the 'Pay' button while the browser is redirecting the user, to
  // prevent the user from creating another payment in the mean time.
  // Unfortunately we can't simply disable the button if replyToWaitlistInviteData is present,
  // because this state object is restored by Safari on pressing the browser back button.
  // In that case, the user should be able to resubmit the checkout.
  const [redirecting, setRedirecting] = useState(false);

  useOnPageCacheRestore(() => {
    // Enable Pay button
    setRedirecting(false);
  });

  const [replyToWaitlistInvite, { error, loading: submitting }] = useMutation(
    ReplyToWaitlistInviteMutation,
    {
      variables: {
        input: {
          waitlist_invite_id: registration.waitlist_invite?.id,
          accept: form.values.accept,
          payment_method: form.values.payment_method,
        },
      },
      onCompleted: ({ replyToWaitlistInvite }) => {
        if (replyToWaitlistInvite) {
          if (replyToWaitlistInvite.status === AddOrderStatus.SUCCESS) {
            if (replyToWaitlistInvite.redirect_url) {
              // Disable the 'Pay' button while redirecting.
              setRedirecting(true);
              window.location.href = replyToWaitlistInvite.redirect_url;
            }
          }
        } else {
          onReject();
        }
      },
      onError: () => {
        //
      },
      refetchQueries: [GetRegistrationSummaryForDashboardQuery, GetRegistrationDetailsQuery],
    },
  );

  const loading = submitting || redirecting;

  const { valid, errors } = useValidation({
    schema: respondToWaitlistInviteSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const invalidPaymentMethod = containsKeyPrefix(errors, 'payment_method');

  const { allowedPaymentMethods } = usePaymentMethods({
    country: registration.country,
    enabledPaymentMethods: event.payment_methods,
    bankTransferAllowed: false,
  });

  return (
    <UI.FormGrid sc={{ padding: [3, 4] }}>
      <UI.Form onSubmit={() => replyToWaitlistInvite()}>
        <UI.FormGrid>
          <UI.ServerErrors error={error} />

          <UI.Div>
            <UI.Legend sc={{ fontWeight: 500, mb: 1 }}>
              <UI.Delimit>
                {t('confirm_ticket', { ticket: waitlistInvite.ticket.title })}
                {waitlistInvite.time_slot && formatTimeSlot(waitlistInvite.time_slot)}
              </UI.Delimit>
            </UI.Legend>
            <Trans
              i18nKey={requirePayment
                ? 'respond_to_waitlist_invite_description'
                : 'respond_to_waitlist_invite_description_free'}
              values={{
                date: formatDate(parseDate(pendingInvite.expires_at, { timezone: 'UTC' })),
              }}
            >
              <UI.Strong />
            </Trans>
          </UI.Div>

          <UI.Table sc={{ borderedRows: true }}>
            <UI.THead>
              <UI.TR>
                <UI.TH colSpan={2}>
                  {t('overview')}
                </UI.TH>
              </UI.TR>
            </UI.THead>
            <UI.TBody>
              <UI.TR>
                <UI.TD>
                  {t('ticket_x', { ticket: waitlistInvite.ticket.title })}
                  {waitlistInvite.ticket.units > 1 && (
                    <UI.Label sc={{ ml: 1 }}>
                      <UI.Icon>
                        <Users />
                      </UI.Icon>
                      {' '}
                      {waitlistInvite.ticket.units}
                    </UI.Label>
                  )}
                </UI.TD>
                <UI.TD sc={{ textAlign: 'right', noWrap: true }}>
                  {formatCurrency(pendingInvite.amount)}
                </UI.TD>
              </UI.TR>
              {pendingInvite.upgrades.map((upgrade) => (
                <UI.TR key={upgrade.id}>
                  <UI.TD>
                    {t('upgrade_x', { upgrade: upgrade.product.title })}
                  </UI.TD>
                  <UI.TD sc={{ textAlign: 'right', noWrap: true }}>
                    {formatCurrency(upgrade.resale_amount)}
                  </UI.TD>
                </UI.TR>
              ))}
              {pendingInvite.fee > 0 && (
                <UI.TR>
                  <UI.TD>
                    {t('platform_fee')}
                  </UI.TD>
                  <UI.TD sc={{ textAlign: 'right', noWrap: true }}>
                    {formatCurrency(pendingInvite.fee)}
                  </UI.TD>
                </UI.TR>
              )}
            </UI.TBody>
            <UI.TBody>
              <UI.TR sc={{ strong: true }}>
                <UI.TD>
                  {t('total')}
                </UI.TD>
                <UI.TD sc={{ textAlign: 'right', noWrap: true }}>
                  {formatCurrency(totalAmount)}
                </UI.TD>
              </UI.TR>
            </UI.TBody>
          </UI.Table>

          <UI.InputGroup sc={{ valid: !errors.accept, touched: !!form.touched.accept }}>
            <UI.InputLabel>
              {t('your_response')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.FormGrid sc={{ gutter: 0.25 }}>
              <UI.Radio {...form.radio('accept', true)} sc={{ box: true, radioTop: 14 }}>
                <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <UI.Div sc={{ fontWeight: 500 }}>
                    {t('accept_ticket')}
                  </UI.Div>
                  <UI.Badge sc={{ size: 24, background: 'success' }} style={{ opacity: !form.values.accept ? 0.8 : 1 }}>
                    <UI.Icon sc={{ size: 18 / 14, color: 'white', mb: 0 }}>
                      <Check />
                    </UI.Icon>
                  </UI.Badge>
                </UI.FlexContainer>
              </UI.Radio>
              <UI.Radio {...form.radio('accept', false)} sc={{ box: true, radioTop: 14 }}>
                <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <UI.Div sc={{ fontWeight: 500 }}>
                    {t('reject_and_stay_on_waitlist')}
                  </UI.Div>
                  <UI.Badge sc={{ size: 24, background: 'error' }} style={{ opacity: form.values.accept ? 0.8 : 1 }}>
                    <UI.Icon sc={{ size: 18 / 14, color: 'white', mb: 0 }}>
                      <X />
                    </UI.Icon>
                  </UI.Badge>
                </UI.FlexContainer>
              </UI.Radio>
            </UI.FormGrid>
          </UI.InputGroup>

          {requirePayment && form.values.accept && (
            <UI.InputGroup sc={{ mb: 0, valid: !invalidPaymentMethod, touched: !!form.touched.payment_method }}>
              <UI.InputLabel>
                {t('common:payments.payment_method')}
                {' '}
                <UI.RequiredMark sc={{ valid: !invalidPaymentMethod }} />
              </UI.InputLabel>
              <PaymentMethodSelection
                value={form.values.payment_method}
                onChange={(paymentMethod) => form.set('payment_method', paymentMethod)}
                paymentMethods={allowedPaymentMethods}
              />
            </UI.InputGroup>
          )}

          <UI.Button
            type="submit"
            sc={{ brand: 'secondary', loading }}
            disabled={loading || !valid}
          >
            {form.values.accept && (
              <>
                <UI.Icon>
                  <Lock />
                </UI.Icon>
                {' '}
                {requirePayment
                  ? t('pay_amount', {
                    amount: formatCurrency(totalAmount),
                  })
                  : t('confirm')}
              </>
            )}
            {!form.values.accept && (
              <>
                {t('confirm')}
              </>
            )}
          </UI.Button>
        </UI.FormGrid>
      </UI.Form>
    </UI.FormGrid>
  );
};

export default RespondToWaitlistInviteForm;
